import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, GraduationCap, Telescope, RefreshCw } from "lucide-react";

const POS_COLORS = { QB: "#0284C7", RB: "#16A34A", WR: "#7C3AED", TE: "#D97706" };
const TIER_COLORS = { Elite: "#16A34A", "High-End": "#0284C7", Mid: "#7C3AED", Developmental: "#6B7280" };

export default function DevyScanner() {
  const [prospects, setProspects] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [graduated, setGraduated] = useState([]);
  const [lastCurated, setLastCurated] = useState(null);
  const [declWindow, setDeclWindow] = useState(null);
  const [pos, setPos] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    api.get("/devy/pick-bridge").then(r => {
      setBridge(r.data.bridge);
      setGraduated(r.data.graduated || []);
      setLastCurated(r.data.last_curated || null);
      setDeclWindow(r.data.declaration_window || null);
    });
  }, []);
  useEffect(() => {
    setProspects(null);
    const params = {};
    if (pos) params.position = pos;
    if (year) params.year = year;
    api.get("/devy/prospects", { params }).then(r => setProspects(r.data.prospects));
  }, [pos, year]);

  const years = [...new Set((prospects || []).map(p => p.draft_year))];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="devy-scanner">
      <div className="flex items-center gap-3 mb-1">
        <Telescope className="w-6 h-6 text-[var(--accent)]" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">Prospect Pipeline</h1>
      </div>
      <p className="text-sm text-[var(--text-2)] mb-6 max-w-2xl">
        Map your future rookie picks to the real college prospects expected in each class. A <span className="text-[var(--accent)]">2027 1st</span> isn't an abstraction — it's Arch Manning's tier.
      </p>

      {/* Pick → Prospect bridge */}
      <div className="mb-8">
        {declWindow?.open && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#FF3B3B]/40 bg-[#FF3B3B]/10 text-sm" data-testid="devy-declaration-banner">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B3B] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B3B]"></span>
            </span>
            <span className="font-semibold text-[#FF6B6B]">Declaration window open</span>
            <span className="text-[var(--text-2)]">— board auto-updates daily as players declare for the draft{declWindow.closes ? ` (through ${declWindow.closes})` : ""}.</span>
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-[var(--accent)] text-xs tracking-widest font-mono">// PICK → PROSPECT BRIDGE</div>
          <div className="inline-flex items-center gap-1.5 text-[11px] text-[var(--text-3)] font-mono" data-testid="devy-autoupdate-note">
            <RefreshCw className="w-3 h-3 text-[var(--accent)]" />
            Self-updating — prospects drop off automatically once they declare & get drafted
          </div>
        </div>
        {bridge === null ? (
          <div className="card-bordered p-8 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {bridge.map(b => (
              <div key={b.season} className="card-bordered p-5" data-testid={`bridge-${b.season}`}>
                <div className="font-mono text-2xl text-white mb-3">{b.season} <span className="text-[var(--text-3)] text-sm">DRAFT</span></div>
                <div className="space-y-3">
                  {b.rounds.map(r => (
                    <div key={r.round}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-[var(--accent)]">{b.season} Rd {r.round}</span>
                        <span className="text-[10px] tracking-widest font-mono" style={{ color: TIER_COLORS[r.tier_label.split(" ")[0]] || "var(--text-3)" }}>{r.tier_label}</span>
                      </div>
                      <div className="text-xs text-[var(--text-2)] mt-1">
                        {r.headliners.length ? r.headliners.map(h => h.name).join(" · ") : "Class not yet projected"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recently graduated to the NFL — proves the board keeps itself current */}
      {graduated.length > 0 && (
        <div className="mb-8" data-testid="devy-graduated">
          <div className="flex items-center gap-2 text-xs tracking-widest mb-3 font-mono text-[var(--text-2)]">
            <GraduationCap className="w-4 h-4 text-[var(--accent)]" />
            // GRADUATED TO THE NFL <span className="text-[var(--text-3)]">— now live on the DME board</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {graduated.map((g, i) => (
              <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--border)] bg-white/5 text-sm" data-testid={`graduated-${i}`}>
                <span className="font-semibold">{g.name}</span>
                <span className="font-mono text-[11px]" style={{ color: POS_COLORS[g.pos] || "var(--text-3)" }}>{g.pos}</span>
                {g.value != null && <span className="font-mono text-[11px] text-[var(--accent)]">{g.value.toLocaleString()}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[var(--accent)] text-xs tracking-widest font-mono">// DEVY BIG BOARD</div>
        <div className="flex gap-2">
          <select className="input-dark text-sm py-1.5 w-auto" value={pos} onChange={e => setPos(e.target.value)} data-testid="devy-pos-filter">
            <option value="">All positions</option>
            {["QB", "RB", "WR", "TE"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="input-dark text-sm py-1.5 w-auto" value={year} onChange={e => setYear(e.target.value)} data-testid="devy-year-filter">
            <option value="">All classes</option>
            {[2027, 2028, 2029].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {prospects === null ? (
        <div className="card-bordered p-8 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>
      ) : (
        <div className="card-bordered overflow-hidden">
          {prospects.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm border-b border-[var(--border)] last:border-0 hover:bg-white/5" data-testid={`prospect-${i}`}>
              <div className="col-span-1 font-mono text-[var(--text-3)]">#{i + 1}</div>
              <div className="col-span-4 min-w-0">
                <div className="truncate font-semibold">{p.name}</div>
                <div className="text-[11px] text-[var(--text-3)]">{p.school} · {p.class}</div>
              </div>
              <div className="col-span-1 font-mono font-bold" style={{ color: POS_COLORS[p.pos] }}>{p.pos}</div>
              <div className="col-span-1 font-mono text-[var(--text-2)]">{p.draft_year}</div>
              <div className="col-span-2">
                <span className="text-[10px] tracking-widest font-mono px-2 py-0.5 rounded-sm border" style={{ color: TIER_COLORS[p.tier], borderColor: TIER_COLORS[p.tier] + "55" }}>{p.tier}</span>
              </div>
              <div className="col-span-3 text-xs text-[var(--text-2)] truncate hidden md:block">{p.note}</div>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11px] text-[var(--text-3)] mt-3">Curated from consensus devy boards (ESPN, CBS, ProDraftScouting, 247Sports){lastCurated ? ` · last curated ${lastCurated}` : ""} · drafted prospects auto-graduate to the live board.</div>
    </div>
  );
}
